import React, { useState, useEffect } from 'react';
import { routineService } from '../services/routineService';
import { FaPlus, FaTrash, FaEdit, FaClock, FaTimes } from 'react-icons/fa';

function Routine() {
  const [dayTypes, setDayTypes] = useState([]);
  const [selectedDayType, setSelectedDayType] = useState(null);
  const [routines, setRoutines] = useState([]);
  const [showDayTypeForm, setShowDayTypeForm] = useState(false);
  const [showRoutineForm, setShowRoutineForm] = useState(false);
  const [editingDayType, setEditingDayType] = useState(null);
  const [editingRoutine, setEditingRoutine] = useState(null);
  const [newDayType, setNewDayType] = useState({ name: '', color: '#3B82F6' });
  const [newRoutine, setNewRoutine] = useState({
    title: 'Sleep',
    startTime: '22:00', // 10 PM
    endTime: '06:00', // 6 AM
    description: 'Night sleep schedule',
    dayTypeId: ''
  });

  const routineTemplates = [
    { title: 'Sleep', startTime: '22:00', endTime: '06:00', description: 'Night sleep schedule' },
    { title: 'Work', startTime: '09:00', endTime: '17:00', description: 'Regular work hours' },
    { title: 'Gym', startTime: '07:00', endTime: '08:00', description: 'Morning workout' },
    { title: 'Study', startTime: '19:00', endTime: '21:00', description: 'Evening learning session' },
    { title: 'Meditation', startTime: '06:30', endTime: '07:00', description: 'Morning meditation' },
    { title: 'Lunch', startTime: '13:00', endTime: '14:00', description: 'Lunch break' }
  ];

  // Fetch day types and routines
  useEffect(() => {
    fetchDayTypes();
  }, []);

  useEffect(() => {
    if (selectedDayType) {
      fetchRoutines(selectedDayType._id);
    }
  }, [selectedDayType]);

  const fetchDayTypes = async () => {
    try {
      const response = await routineService.getDayTypes();
      setDayTypes(response.data);
    } catch (error) {
      console.error('Error fetching day types:', error);
    }
  };

  const fetchRoutines = async (dayTypeId) => {
    try {
      const response = await routineService.getRoutines(dayTypeId);
      setRoutines(response.data);
    } catch (error) {
      console.error('Error fetching routines:', error);
    }
  };

  // Day Type CRUD operations
  const handleAddDayType = async (e) => {
    e.preventDefault();
    try {
      if (!newDayType.name.trim()) {
        alert('Please enter a name for the day type');
        return;
      }

      const response = await routineService.createDayType({
        name: newDayType.name,
        color: newDayType.color,
        createdBy: localStorage.getItem('userId')
      });

      if (response.data) {
        setDayTypes([...dayTypes, response.data]);
        setShowDayTypeForm(false);
        setNewDayType({ name: '', color: '#3B82F6' });
      }
    } catch (error) {
      console.error('Error adding day type:', error);
      alert('Failed to add day type. Please try again.');
    }
  };

  const handleUpdateDayType = async (e) => {
    e.preventDefault();
    try {
        if (!editingDayType._id) {
            throw new Error('Day type ID is missing');
        }

        // Add debug logs
        console.log('Updating day type:', editingDayType);

        const response = await routineService.updateDayType(editingDayType._id, {
            name: editingDayType.name,
            color: editingDayType.color
        });

        if (response.data) {
            setDayTypes(prevDayTypes => 
                prevDayTypes.map(dt => 
                    dt._id === editingDayType._id ? response.data : dt
                )
            );
            setEditingDayType(null);
            // Refresh day types after update
            fetchDayTypes();
        }
    } catch (error) {
        console.error('Error updating day type:', error);
        alert(error.message || 'Failed to update day type. Please try again.');
    }
  };

  const handleDeleteDayType = async (id) => {
    try {
      await routineService.deleteDayType(id);
      setDayTypes(dayTypes.filter(dt => dt._id !== id));
      if (selectedDayType?._id === id) {
        setSelectedDayType(null);
      }
    } catch (error) {
      console.error('Error deleting day type:', error);
    }
  };

  const timeSlots = Array.from({ length: 24 }, (_, i) => {
    const hour = i.toString().padStart(2, '0');
    return `${hour}:00`;
  });

  const handleAddRoutine = async (e) => {
    e.preventDefault();
    try {
      const response = await routineService.createRoutine(newRoutine);
      setRoutines([...routines, response.data]);
      setShowRoutineForm(false);
      setNewRoutine({
        title: '',
        startTime: '',
        endTime: '',
        description: '',
        dayTypeId: selectedDayType
      });
    } catch (error) {
      console.error('Error adding routine:', error);
    }
  };

  // Add template selection handler
  const handleTemplateSelect = (template) => {
    setNewRoutine({
      ...newRoutine,
      ...template,
      dayTypeId: selectedDayType?._id
    });
  };

  const getTimeSlots = () => {
    return Array.from({ length: 24 }, (_, i) => {
      const hour = i.toString().padStart(2, '0');
      return `${hour}:00`;
    });
  };

  const isEventInTimeSlot = (event, timeSlot) => {
    const slotHour = parseInt(timeSlot.split(':')[0]);
    const startHour = parseInt(event.startTime.split(':')[0]);
    const endHour = parseInt(event.endTime.split(':')[0]);

    // Handle overnight events (end time less than start time)
    if (endHour < startHour) {
      return slotHour >= startHour || slotHour < endHour;
    }
    
    return slotHour >= startHour && slotHour < endHour;
  };

  const getEventsForTimeSlot = (time, events) => {
    const hour = parseInt(time.split(':')[0]);
    return events.filter(event => {
      const startHour = parseInt(event.startTime.split(':')[0]);
      const endHour = parseInt(event.endTime.split(':')[0]);
      
      // Handle overnight events
      if (endHour < startHour) {
        return hour >= startHour || hour < endHour;
      }
      return hour >= startHour && hour < endHour;
    });
  };

  return (
    <div className="p-6 max-w-[1600px] mx-auto">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-2xl font-bold text-gray-800">Daily Routines</h1>
        <button
          onClick={() => setShowDayTypeForm(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Add Day Type
        </button>
      </div>

      {/* Day Types List */}
      <div className="flex gap-4 mb-8 overflow-x-auto pb-4">
        {dayTypes.map(type => (
          <div
            key={type._id}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg cursor-pointer
              ${selectedDayType?._id === type._id ? 'ring-2 ring-blue-500' : ''}
            `}
            style={{ backgroundColor: type.color + '20' }}
            onClick={() => setSelectedDayType(type)}
          >
            <span className="font-medium">{type.name}</span>
            <div className="flex gap-1">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setEditingDayType(type);
                }}
                className="p-1 text-gray-600 hover:text-blue-600"
              >
                <FaEdit className="w-4 h-4" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteDayType(type._id);
                }}
                className="p-1 text-gray-600 hover:text-red-600"
              >
                <FaTrash className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Timeline View */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="grid grid-cols-[100px_1fr] gap-4">
          {getTimeSlots().map((time, index) => (
            <React.Fragment key={time}>
              <div className="text-sm text-gray-500">{time}</div>
              <div className={`min-h-[4rem] border-t ${
                index === 0 ? '' : 'border-gray-100'
              }`}>
                <div className="flex gap-2">
                  {routines.map(routine => 
                    getEventsForTimeSlot(time, routine.events)
                      .map((event, eventIndex) => (
                        <div
                          key={event._id}
                          className="flex-1 p-2 rounded text-sm"
                          style={{
                            backgroundColor: selectedDayType?.color + '20' || '#EFF6FF',
                            maxWidth: '200px'
                          }}
                        >
                          <div className="font-medium truncate">{event.title}</div>
                          <div className="text-xs text-gray-600">
                            {event.startTime} - {event.endTime}
                          </div>
                        </div>
                      ))
                  )}
                </div>
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Add Routine Button */}
      <button
        onClick={() => setShowRoutineForm(true)}
        className="fixed bottom-6 right-6 bg-blue-600 text-white p-4 rounded-full shadow-lg hover:bg-blue-700"
      >
        <FaPlus />
      </button>

      {/* Add Routine Form Modal */}
      {showRoutineForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Add New Routine</h2>
              <button
                onClick={() => setShowRoutineForm(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <FaTimes />
              </button>
            </div>

            {/* Quick Templates */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Quick Templates
              </label>
              <div className="grid grid-cols-2 gap-2">
                {routineTemplates.map((template, index) => (
                  <button
                    key={index}
                    onClick={() => handleTemplateSelect(template)}
                    className="p-2 text-sm text-left border rounded hover:bg-gray-50"
                  >
                    <div className="font-medium">{template.title}</div>
                    <div className="text-gray-500 text-xs">
                      {template.startTime} - {template.endTime}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <form onSubmit={handleAddRoutine} className="space-y-4">
              <input
                type="text"
                placeholder="Activity Title"
                value={newRoutine.title}
                onChange={(e) => setNewRoutine({...newRoutine, title: e.target.value})}
                className="w-full p-2 border rounded"
                required
              />
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Start Time</label>
                  <input
                    type="time"
                    value={newRoutine.startTime}
                    onChange={(e) => setNewRoutine({...newRoutine, startTime: e.target.value})}
                    className="w-full p-2 border rounded"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">End Time</label>
                  <input
                    type="time"
                    value={newRoutine.endTime}
                    onChange={(e) => setNewRoutine({...newRoutine, endTime: e.target.value})}
                    className="w-full p-2 border rounded"
                    required
                  />
                </div>
              </div>
              <textarea
                placeholder="Description (optional)"
                value={newRoutine.description}
                onChange={(e) => setNewRoutine({...newRoutine, description: e.target.value})}
                className="w-full p-2 border rounded"
                rows="3"
              />
              <div className="flex gap-2 justify-end">
                <button
                  type="button"
                  onClick={() => setShowRoutineForm(false)}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Add Routine
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Day Type Form Modal */}
      {showDayTypeForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Add New Day Type</h2>
              <button
                onClick={() => setShowDayTypeForm(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <FaTimes />
              </button>
            </div>
            <form onSubmit={handleAddDayType} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name
                </label>
                <input
                  type="text"
                  placeholder="e.g., Working Day, Holiday"
                  value={newDayType.name}
                  onChange={(e) => setNewDayType({...newDayType, name: e.target.value})}
                  className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Color
                </label>
                <div className="flex gap-2 items-center">
                  <input
                    type="color"
                    value={newDayType.color}
                    onChange={(e) => setNewDayType({...newDayType, color: e.target.value})}
                    className="h-10 w-20"
                  />
                  <span className="text-sm text-gray-500">
                    Pick a color for this day type
                  </span>
                </div>
              </div>
              <div className="flex gap-2 justify-end pt-4">
                <button
                  type="button"
                  onClick={() => setShowDayTypeForm(false)}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Add Day Type
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Day Type Form Modal */}
      {editingDayType && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Edit Day Type</h2>
              <button
                onClick={() => setEditingDayType(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                <FaTimes />
              </button>
            </div>
            <form onSubmit={handleUpdateDayType} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name
                </label>
                <input
                  type="text"
                  placeholder="e.g., Working Day, Holiday"
                  value={editingDayType.name}
                  onChange={(e) => setEditingDayType({
                    ...editingDayType,
                    name: e.target.value
                  })}
                  className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Color
                </label>
                <div className="flex gap-2 items-center">
                  <input
                    type="color"
                    value={editingDayType.color}
                    onChange={(e) => setEditingDayType({
                      ...editingDayType,
                      color: e.target.value
                    })}
                    className="h-10 w-20"
                  />
                  <span className="text-sm text-gray-500">
                    Pick a color for this day type
                  </span>
                </div>
              </div>
              <div className="flex gap-2 justify-end pt-4">
                <button
                  type="button"
                  onClick={() => setEditingDayType(null)}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Update Day Type
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Routine;
